import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  AlertCircle,
  Flame,
  MapPin,
  CalendarClock,
  Cpu,
  Globe,
  Link2,
  Share2,
  UserPlus,
  Clock,
  Download,
  Layers,
  MousePointerClick,
  RefreshCw,
  Smartphone,
  Users,
} from "lucide-react";
import {
  API,
  Button,
  Card,
  EmptyState,
  PageHeader,
  SegmentedControl,
  Skeleton,
  apiFetch,
  cx,
} from "./_shared";
import { BarList, ChartCard, KpiCard, LineChart, StackedBar, VIZ, fmtNum } from "../../components/admin/charts";
import { HourHeatmap, LivePanel } from "../../components/admin/traffic/TrafficExtras";
import { CalendarHeatmap, DonutChart } from "../../components/admin/chartsExtra";
import { GRANULARITY_UNIT, formatBucket, formatFullDate, rangeText } from "../../components/admin/timeSeries";
import TrafficRangePicker, { DEFAULT_RANGE, rangeQuery } from "../../components/admin/TrafficRangePicker";

const METRICS = [
  { value: "pageviews", label: "Lượt xem trang", color: VIZ[0] },
  { value: "sessions", label: "Phiên truy cập", color: VIZ[1] },
  { value: "visitors", label: "Khách", color: VIZ[2] },
];

const DEVICE_LABEL = { mobile: "Điện thoại", desktop: "Máy tính", tablet: "Máy tính bảng" };

function fmtDuration(sec) {
  const s = Math.round(Number(sec) || 0);
  if (s <= 0) return "—";
  if (s < 60) return `${s} giây`;
  const m = Math.floor(s / 60);
  return `${m} phút${s % 60 ? ` ${String(s % 60).padStart(2, "0")} giây` : ""}`;
}

const PALETTE = ["var(--viz-1)", "var(--viz-2)", "var(--viz-3)", "#7c5cc4", "#1aa6b7", "#d6538f", "#a3a948"];
/** Giữ top N phần, gộp phần còn lại thành "Khác"; gán màu cố định (palette gốc chỉ có 3 màu). */
function topWithOther(rows = [], keep = 4) {
  const sorted = [...rows].sort((a, b) => b.count - a.count);
  const head = sorted.slice(0, keep);
  const rest = sorted.slice(keep).reduce((a, r) => a + r.count, 0);
  const merged = head.filter((r) => r.label !== "Khác").map((r) => ({ label: r.label, value: r.count }));
  const other = rest + head.filter((r) => r.label === "Khác").reduce((a, r) => a + r.count, 0);
  if (other > 0) merged.push({ label: "Khác", value: other });
  return merged.map((p, i) => ({ ...p, color: p.label === "Khác" ? "#c9ced6" : PALETTE[i % PALETTE.length] }));
}

const sum = (rows, k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);

// ─── Dữ liệu ─────────────────────────────────────────────────────────────────
function useTrafficReport(range) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const seq = useRef(0);

  const load = useCallback(
    async ({ fresh = false } = {}) => {
      const id = ++seq.current;
      fresh ? setRefreshing(true) : setLoading(true);
      setError("");
      try {
        const res = await apiFetch(
          `${API}/analytics/admin/traffic?${rangeQuery(range)}${fresh ? "&refresh=1" : ""}`
        );
        if (id === seq.current) setData(res);
      } catch (e) {
        if (id === seq.current) setError(e?.message || "Không tải được thống kê truy cập");
      } finally {
        if (id === seq.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [range]
  );

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, refreshing, error, reload: load };
}

// ─── Bảng lượt xem theo địa điểm (có tìm kiếm, sắp xếp, phân trang) ──────────
function exportCsv(report) {
  const g = report.range.granularity;
  const head = g === "month" ? "Tháng" : g === "week" ? "Tuần (từ thứ Hai)" : "Ngày";
  const rows = [[head, "Lượt xem trang", "Phiên truy cập", "Khách", "Lượt xem địa điểm"], ...report.series.map((d) => [d.key, d.pageviews ?? "", d.sessions, d.visitors, d.locationViews])];
  const blob = new Blob(["﻿" + rows.map((r) => r.join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `truy-cap_${report.range.from}_${report.range.to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Trang ───────────────────────────────────────────────────────────────────
function AdminOverview() {
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [metric, setMetric] = useState("pageviews");
  const { data, loading, refreshing, error, reload } = useTrafficReport(range);

  const t = data?.totals;
  const d = data?.deltas || {};
  const daily = useMemo(() => data?.series || [], [data]);
  const g = data?.range?.granularity || "day";
  const unit = GRANULARITY_UNIT[g];
  const fmtX = useCallback((k, long) => formatBucket(k, g, long), [g]);
  const isAll = data?.range?.days === "all";
  const nDays = data?.range?.nDays;
  const compareLabel = isAll || !nDays ? undefined : `so với ${nDays} ngày trước`;
  const m = METRICS.find((x) => x.value === metric);
  const trendOf = (key) => daily.map((x) => x[key]);

  const series = useMemo(
    () => [
      {
        label: m.label,
        values: daily.map((x) => x[metric]),
        flags: metric === "pageviews" ? daily.map((x) => (x.pageviewsEstimated ? "ước tính" : null)) : undefined,
        color: m.color,
      },
    ],
    [daily, metric, m]
  );

  const bestDay = useMemo(() => daily.reduce((best, x) => (x[metric] != null && x[metric] > (best?.[metric] ?? -1) ? x : best), null), [daily, metric]);
  const withData = daily.filter((x) => x[metric] != null);
  const avgPerDay = withData.length ? Math.round(sum(withData, metric) / withData.length) : 0;

  const devices = (data?.devices || []).map((x) => ({ label: DEVICE_LABEL[x.label] || x.label, value: x.count }));
  const sources = (data?.sources || []).map((x) => ({ label: x.label === "Direct" ? "Truy cập trực tiếp" : x.label === "Referrer" ? "Từ trang khác" : x.label, value: x.count }));
  const aud = data?.audience || { new: 0, returning: 0 };
  // slug → tên địa điểm (để hiện tên thay cho đường dẫn /location/slug)
  const nameBySlug = useMemo(() => new Map((data?.topLocations || []).map((l) => [l.slug, l.name])), [data]);

  return (
    <div className="space-y-6 pb-4">
      <PageHeader
        title="Truy cập & lượt xem"
        subtitle={data ? `${rangeText(data.range.from, data.range.to, data.range.nDays)}${isAll ? " · toàn bộ lịch sử" : ` · so với ${nDays} ngày trước đó`}` : "Theo dõi lưu lượng truy cập website"}
        action={
          <>
            <Button variant="secondary" icon={Download} onClick={() => data && exportCsv(data)} disabled={!data} title="Tải số liệu theo ngày (CSV)">
              Xuất CSV
            </Button>
            <Button variant="secondary" icon={RefreshCw} onClick={() => reload({ fresh: true })} loading={refreshing}>
              Làm mới
            </Button>
          </>
        }
      />

      {/* Thanh chọn khoảng thời gian: dính ngay trên các biểu đồ, cuộn tới đâu cũng thấy */}
      <div className="sticky top-14 z-30 -mx-1 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-admin-border bg-admin-surface/95 px-3 py-2.5 shadow-soft backdrop-blur">
        <span className="text-sm font-semibold text-admin-text">Khoảng thời gian</span>
        <TrafficRangePicker value={range} onChange={setRange} align="left" />
        {refreshing ? <span className="text-xs text-admin-textMuted">Đang cập nhật…</span> : null}
      </div>

      {error && !data ? (
        <Card>
          <EmptyState
            icon={AlertCircle}
            title="Không tải được thống kê"
            description={error}
            action={
              <Button variant="primary" icon={RefreshCw} onClick={() => reload({ fresh: true })}>
                Thử lại
              </Button>
            }
          />
        </Card>
      ) : loading && !data ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 xl:grid-cols-3 2xl:grid-cols-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-[150px] rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-[380px] rounded-xl" />
          <div className="grid gap-6 xl:grid-cols-3">
            <Skeleton className="h-72 rounded-xl xl:col-span-2" />
            <Skeleton className="h-72 rounded-xl" />
          </div>
        </div>
      ) : data ? (
        <div className={cx("space-y-6 transition-opacity", (loading || refreshing) && "opacity-60")}>
          {/* KPI */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard tone="blue" icon={MousePointerClick} label="Lượt xem trang" value={t.pageviews == null ? "—" : fmtNum(t.pageviews)} delta={d.pageviews ?? undefined} deltaLabel={compareLabel} hint={data.pageviewsSince ? `Lưu từ ${formatFullDate(data.pageviewsSince)}` : undefined} trend={trendOf("pageviews")} />
            <KpiCard tone="violet" icon={Layers} label="Phiên truy cập" value={fmtNum(t.sessions)} delta={d.sessions ?? undefined} deltaLabel={compareLabel} trend={trendOf("sessions")} />
            <KpiCard tone="cyan" icon={Users} label="Khách truy cập" value={fmtNum(t.visitors)} delta={d.visitors ?? undefined} deltaLabel={compareLabel} trend={trendOf("visitors")} />
            <KpiCard
              tone="amber"
              icon={Clock}
              label="Thời gian trung bình"
              value={fmtDuration(t.avgDuration)}
              delta={t.avgDuration && d.avgDuration != null ? d.avgDuration : undefined}
              deltaLabel={compareLabel}
              hint="Mỗi phiên, tối đa 30 phút"
            />
          </div>

          {/* Phiên truy cập gần nhất */}
          <LivePanel nameBySlug={nameBySlug} />

          {/* Biểu đồ chính */}
          <ChartCard
            icon={Activity}
            tone="blue"
            title={`Lưu lượng theo ${unit}`}
            subtitle={
              bestDay
                ? `Trung bình ${fmtNum(avgPerDay)}/${unit} · cao nhất ${fmtNum(bestDay[metric])} (${fmtX(bestDay.key, true)})`
                : undefined
            }
            action={
              <div className="flex flex-wrap items-center justify-end gap-2">
                <SegmentedControl ariaLabel="Chỉ số" value={metric} onChange={setMetric} options={METRICS.map((x) => ({ value: x.value, label: x.label }))} />
              </div>
            }
            table={{
              columns: [g === "month" ? "Tháng" : g === "week" ? "Tuần" : "Ngày", "Lượt xem trang", "Phiên", "Khách"],
              rows: daily.map((x) => [fmtX(x.key, true), x.pageviews == null ? "—" : fmtNum(x.pageviews), fmtNum(x.sessions), fmtNum(x.visitors)]),
            }}
          >
            <LineChart labels={daily.map((x) => x.key)} series={series} height={300} formatLabel={fmtX} />
            {metric === "pageviews" && !data.pageviewsEstimatedUntil && data.pageviewsSince ? (
              <p className="mt-3 text-xs text-admin-textMuted">
                Lượt xem trang bắt đầu được lưu đầy đủ từ {formatFullDate(data.pageviewsSince)} — trước ngày này biểu đồ để trống.
              </p>
            ) : null}
          </ChartCard>

          {/* Lịch lưu lượng + khách mới/cũ */}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <ChartCard
              icon={CalendarClock}
              tone="cyan"
              className="xl:col-span-2"
              title="Lưu lượng theo ngày"
              subtitle={`${m.label} mỗi ngày trong 12 tháng tính đến ${formatFullDate(data.range.to)} · ô đậm là khoảng đang chọn`}
              action={<SegmentedControl ariaLabel="Chỉ số" value={metric} onChange={setMetric} options={METRICS.map((x) => ({ value: x.value, label: x.label }))} />}
            >
              <CalendarHeatmap
                days={(data.calendar || []).map((x) => ({
                  date: x.date,
                  // Ngày không có phiên nào thì lượt xem trang cũng là 0 (kể cả trước khi bắt đầu đo)
                  value: x[metric] == null && x.sessions === 0 ? 0 : x[metric],
                  dim: x.inRange === false,
                }))}
                unit={metric === "pageviews" ? "lượt xem" : metric === "visitors" ? "khách" : "phiên"}
              />
            </ChartCard>

            <div className="flex flex-col gap-6">
              <ChartCard icon={UserPlus} tone="violet" className="flex-1" title="Khách mới & khách quay lại" subtitle="Dựa trên mã trình duyệt của khách · đã loại bot">
                <StackedBar
                  parts={[
                    { label: "Khách mới", value: aud.new, color: VIZ[0] },
                    { label: "Khách quay lại", value: aud.returning, color: VIZ[1] },
                  ]}
                />
              </ChartCard>
            </div>
          </div>

          {/* Thiết bị */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <ChartCard icon={Smartphone} tone="cyan" title="Thiết bị" subtitle="Loại thiết bị của khách">
              <DonutChart parts={devices} centerLabel="Phiên" size={150} />
            </ChartCard>
            <ChartCard icon={Cpu} tone="slate" title="Hệ điều hành">
              <DonutChart parts={topWithOther(data.os)} centerLabel="Phiên" size={150} />
            </ChartCard>
            <ChartCard icon={Globe} tone="blue" title="Trình duyệt" subtitle="Gồm cả trình duyệt trong ứng dụng (Facebook, Zalo…)">
              <StackedBar parts={topWithOther(data.browsers, 6)} />
            </ChartCard>
          </div>

          {/* Giờ cao điểm + địa điểm xem nhiều trong khoảng */}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <ChartCard icon={Flame} tone="amber" className="xl:col-span-2" title="Giờ cao điểm" subtitle="Số phiên theo thứ trong tuần × giờ trong ngày (giờ Việt Nam) · khoảng đang chọn">
              <HourHeatmap heat={data.heat} />
            </ChartCard>
            <ChartCard icon={MapPin} tone="emerald" title="Địa điểm xem nhiều" subtitle="Lượt xem trang chi tiết trong khoảng đang chọn">
              <BarList items={(data.topLocations || []).map((l) => ({ key: l.id, label: l.name, value: l.views }))} empty="Chưa có lượt xem địa điểm" />
            </ChartCard>
          </div>

          {/* Nguồn truy cập */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <ChartCard icon={Share2} tone="violet" title="Nguồn truy cập" subtitle="Khách đến từ đâu">
              <DonutChart parts={sources} centerLabel="Phiên" size={150} />
            </ChartCard>
            <ChartCard icon={Link2} tone="emerald" title="Trang giới thiệu hàng đầu" subtitle="Website khác dẫn khách tới">
              <DonutChart parts={topWithOther(data.referrers, 4)} centerLabel="Phiên" size={150} empty="Chưa có nguồn giới thiệu" />
            </ChartCard>
          </div>

        </div>
      ) : null}

    </div>
  );
}

export default AdminOverview;
